// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { FlameRow } from "../ChildHero.jsx";
import { DoubledTokens, Celebration, Icon, PhotoProofModal, Piggington, uploadResizedImage } from "../Common.jsx";
import { isDoubleTokenDay } from "../../lib/dailyQuestion.js";
import { t as ct } from "../../lib/content.js";
import { imageSrc } from "../../lib/origin.js";
import { currentHabitStreak, habitWeekProgress, habitsForChild, isHabitDayComplete, todayStr } from "../../lib/utils.js";
import { DoubleTokenDayBanner } from "./kidShared.jsx";
import { LearnAdvert } from "./LearnTab.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// The daily-habit tracker shown at the top of the kid's Earn section. Checking off every
// applicable task for the day extends the streak; enough complete days in a week (parent-set
// threshold) auto-raises a reward request via onToggleHabit — no separate "claim" step.

export function StreakCard({ child, habits, habitLog, requests, onToggleHabit, showTasks, code }) {
  const [celebrate, setCelebrate] = useState(null); // null | "day" | "week"
  // Which task (if any) is waiting on a photo before it can be checked off — only ever set for
  // the "marking complete" direction; un-checking a task never needs a photo.
  const [photoProofTask, setPhotoProofTask] = useState(null);
  const today = todayStr();
  const applicable = habitsForChild(habits, child.id);
  const doneToday = new Set(habitLog.filter((l) => l.childId === child.id && l.date === today).map((l) => l.taskId));
  const streak = currentHabitStreak(habitLog, habits, child.id, today);
  const { weekStart, completeDays } = habitWeekProgress(habitLog, habits, child.id, today);
  const threshold = child.habitThreshold || 7;
  const pendingReward = requests.find((r) => r.requestType === "habit_reward" && r.childId === child.id && r.status === "pending");
  // Once a parent has approved this week's streak reward, the tasks that earned it lock —
  // a kid can't untick (or tick anything new) for a week that's already been paid out.
  const approvedThisWeek = requests.some((r) => r.requestType === "habit_reward" && r.childId === child.id && r.weekStart === weekStart && r.status === "approved");
  const dayAlreadyComplete = applicable.length > 0 && applicable.every((h) => doneToday.has(h.id));

  useEffect(() => {
    if (!celebrate) return;
    const t = setTimeout(() => setCelebrate(null), 3200);
    return () => clearTimeout(t);
  }, [celebrate]);

  function commitToggle(taskId, willBeDone, image) {
    const nextDone = new Set(doneToday);
    if (willBeDone) nextDone.add(taskId); else nextDone.delete(taskId);
    const willCompleteDay = applicable.length > 0 && applicable.every((h) => nextDone.has(h.id));
    onToggleHabit(child.id, taskId, today, image);
    if (willBeDone && willCompleteDay) {
      const willReachThreshold = completeDays + 1 >= threshold && completeDays < threshold;
      setCelebrate(willReachThreshold ? "week" : "day");
    }
  }

  function handleToggle(taskId) {
    const willBeDone = !doneToday.has(taskId);
    const task = applicable.find((h) => h.id === taskId);
    if (willBeDone && task?.requirePhoto) {
      setPhotoProofTask(task);
      return;
    }
    commitToggle(taskId, willBeDone);
  }

  if (applicable.length === 0) {
    // Journey review P2#2 — copy is Alan's, verbatim. Reverts to the live card the moment
    // a task exists; sprite flame per the proper-icons rule for new/updated surfaces.
    return (
      <div className="mb-1 py-2 px-0.5 flex gap-3 items-start" data-testid="streak-empty">
        <Piggington expression="fx-thinking" className="h-12 w-auto flex-shrink-0" />
        <p className="text-base text-slate-400">{ct("kid.streak.no_tasks", "Ask your grown-up to set up your daily tasks — then earn a bonus every week.")}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {celebrate && <Celebration variant="coins" count={celebrate === "week" ? 28 : 14} />}
      {/* LB-164 (pass H): rows on the canvas. Streak first (flames right), then the tasks. */}
      {/* White sheet (9 Sep): the block carries the accent only while tasks are waiting, and
          the sub-line says how many — the number a child acts on (Alan, item 4). */}
      <div className={`kid-rows kc-streak ${dayAlreadyComplete ? "" : "kc-now"}`} style={{ "--fam": "var(--kid-pop)" }}>
        {/* Alignment pass (10 Sep): title and flames share the line; the sub-line runs the
            full width beneath, so it never wraps under the flames. */}
        <div className="kid-row flex-col items-stretch">
          <span className="flex items-center justify-between gap-2 min-w-0">
            <span className="flex items-center gap-2 min-w-0 font-extrabold text-[15px] text-slate-900"><Icon id="ic-flame" size={18} className="flex-shrink-0" style={{ color: "var(--kid-pop)" }} />{streak} day streak · {completeDays}/{threshold} this week</span>
            <FlameRow total={threshold} filledCount={completeDays} justFilled={!!celebrate} size={16} />
          </span>
          <span className="block text-xs text-slate-500 mt-0.5 pl-[26px]">{!dayAlreadyComplete && applicable.length > 0 ? ct("kid.streak.tasks_waiting", "{count} task{plural} waiting today", { count: applicable.length - doneToday.size, plural: applicable.length - doneToday.size === 1 ? "" : "s" }) + " · " : ""}{completeDays >= threshold ? "Bonus unlocked this week" : `${threshold - completeDays} more day${threshold - completeDays === 1 ? "" : "s"} for +${child.habitReward || 20} tokens`}</span>
        </div>
      {showTasks && (
        <>
          {/* W4 — human nudge, not system gate (workplan decision): the week is DONE and
              the kid should feel it; the remaining step is framed as showing a person, not
              as the app withholding. Same request underneath — only the story changed. */}
          {pendingReward ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center" data-testid="streak-nudge">
              <div className="text-sm font-bold text-amber-700">{ct("kid.streak.nudge_title", "You did the whole week")}</div>
              <div className="text-xs text-amber-600">{ct("kid.streak.nudge_body", "Show a grown-up and ask them to check your week — your +{amount} tokens are ready for them to give you.", { amount: pendingReward.requestedAmount })}</div>
            </div>
          ) : approvedThisWeek ? (
            <>
              <div className="kid-rows opacity-80">
                {applicable.map((h) => (
                  <div key={h.id} className="kid-row justify-start text-sm font-semibold text-slate-700">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500 text-white"><Icon id="ic-check" size={12} style={{ color: "currentColor" }} /></span>
                    {h.label}
                  </div>
                ))}
              </div>
              <p className="text-base text-slate-400 text-center mt-2"><Icon id="ic-lock" size={14} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />This week's tasks are locked in — reward already given</p>
            </>
          ) : (
            <div className="kid-rows">
              {applicable.map((h) => {
                const done = doneToday.has(h.id);
                return (
                  <button key={h.id} onClick={() => handleToggle(h.id)}
                    className={`kid-row w-full justify-start text-left text-sm font-bold transition-colors ${done ? "text-slate-900" : "text-slate-700"}`}>
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"}`}>{done ? <Icon id="ic-check" size={12} style={{ color: "currentColor" }} /> : null}</span>
                    {h.label}{h.requirePhoto && !done ? <Icon id="ic-photo" size={12} className="inline-block align-[-2px] ml-1" style={{ color: "currentColor" }} /> : ""}
                  </button>
                );
              })}
            </div>
          )}
          {dayAlreadyComplete && !pendingReward && !approvedThisWeek && (
            <p className="text-base text-emerald-600 font-semibold text-center mt-2">{ct("kid.all_done_today", "All done for today.")}</p>
          )}
        </>
      )}
      </div>
      {photoProofTask && (
        <PhotoProofModal
          code={code}
          title={photoProofTask.label}
          onCancel={() => setPhotoProofTask(null)}
          onSubmit={(image) => { const t = photoProofTask; setPhotoProofTask(null); commitToggle(t.id, true, image); }}
        />
      )}
    </div>
  );
}

/* --------------------------- Earn tab (kid) --------------------------- */

// Daily Tasks on Earn — collapsed by default. The streak summary (flame, day count, week bar)
// stays visible even collapsed so a kid can check progress at a glance; the actual task
// checklist only renders once expanded. Needs its own open state (rather than the generic
// CollapsibleSection) since that state has to reach into StreakCard's showTasks prop.

export function DailyTasksSection({ code, child, habits, habitLog, requests, onToggleHabit, startExpanded }) {
  const [open, setOpen] = useState(!!startExpanded);
  const todayDone = isHabitDayComplete(habitLog, habits, child.id, todayStr());
  // LB-164 (pass H): the section IS the task list — heading on the canvas, check rows,
  // then the streak row. Always open; the "Nail every task…" explainer is gone, the one line
  // that matters ("N more days for +R tokens") lives on the streak row inside StreakCard.
  return (
    <div data-testid="daily-tasks-section">
      {/* White sheet (9 Sep): no header above a block that has its own title — StreakCard's
          streak line, with "N tasks waiting today", is the header. */}
      <StreakCard child={child} habits={habits} habitLog={habitLog} requests={requests} onToggleHabit={onToggleHabit} showTasks={true} code={code} />
    </div>
  );
}

// "Claim your earnings!" on Earn — the quick-claim pills are visible even collapsed, so a kid
// can tap a familiar activity without opening anything; only the "Ask for tokens" free-form
// request (for something not on the pinned list) needs the section expanded first.

export function ClaimEarningsSection({ allEarnable, pendingClaims, onSubmitRequest, kidTheme, code, earnDoubleDay = false }) {
  const [open, setOpen] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  // Voluntary, never required — a kid can attach a photo to an ad-hoc "did something else"
  // request even though no reward config exists for this free-form flow to hang a requirePhoto
  // flag off. Cleared on submit or cancel same as the text fields above.
  const [askImage, setAskImage] = useState(null);
  const [askImageError, setAskImageError] = useState("");
  const [askImageUploading, setAskImageUploading] = useState(false);
  // Which pinned activity (if any) is waiting on a photo before its claim can submit — set
  // when a kid taps a quick-claim pill with requirePhoto on; cleared on submit or cancel.
  const [photoProofActivity, setPhotoProofActivity] = useState(null);

  function openForAsk() {
    setOpen(true);
    setShowAsk(true);
  }

  async function handleAskPhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setAskImageError(""); setAskImageUploading(true);
    try {
      const url = await uploadResizedImage(code, file, 600, 0.85);
      setAskImage(url);
    } catch (err) {
      setAskImageError(err.message || "Couldn't use that photo.");
    } finally {
      setAskImageUploading(false);
    }
  }

  return (
    <div>
    <div className={`kc kc-gold rounded-2xl p-4 ${!open ? "cursor-pointer" : ""}`} onClick={() => { if (!open) setOpen(true); }}>
      <button className="w-full flex items-center justify-between" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} aria-label={open ? "Collapse" : "Expand"}>
        <span className="font-extrabold kc-title text-base flex items-center gap-2">
          <span className="kc-disc" aria-hidden="true"><Icon id="ic-medal" size={16} style={{ color: "currentColor" }} /></span>{ct("kid.earn.section_claim", "Claim your earnings")}
          {pendingClaims > 0 && <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded"><Icon id="ic-pending" size={12} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{pendingClaims} pending</span>}
        </span>
        <span className="text-slate-400 text-xs"><Icon id={open ? "ic-chevron-up" : "ic-chevron-down"} size={14} className="inline-block" style={{ color: "currentColor" }} /></span>
      </button>
      <p className="text-xs text-slate-500 mt-0.5">{ct("kid.request_intro", "Tell your parents what you did. Tap one, or add your own")}</p>
      {/* The badge sits on the header once rather than on every chip (Alan, 15 Sep) — the chips
          are narrow and repeating it on each would crowd them out. */}
      {earnDoubleDay && <p className="text-[11px] font-bold text-orange-600 mt-1" data-testid="earn-double-banner">{ct("kid.earn.double_day", "Double token day. Everything here pays twice.")}</p>}

      {allEarnable.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {allEarnable.map((a) => (
            <button key={a.id} onClick={(e) => { e.stopPropagation(); if (a.requirePhoto) setPhotoProofActivity(a); else onSubmitRequest(a.label, a.tokens); }}
              className="rounded-lg border border-slate-200 bg-white text-slate-900 text-xs font-bold px-3 py-1.5">{a.requirePhoto ? <Icon id="ic-photo" size={12} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} /> : ""}{a.label} {earnDoubleDay ? <DoubledTokens base={a.tokens} /> : `+${a.tokens}`}</button>
          ))}
        </div>
      )}

      {!open && (
        <button className="bg-pill text-navy text-xs font-bold rounded-lg px-3 py-2 mt-3" onClick={(e) => { e.stopPropagation(); openForAsk(); }}>
          Did something else? Tell them here
        </button>
      )}

      {open && (
        <div className={allEarnable.length > 0 ? "pt-3" : "pt-1"}>
          {!showAsk ? (
            <button onClick={() => setShowAsk(true)} className="w-full bg-emerald-50 text-slate-900 font-bold text-sm rounded-xl py-2.5">{ct("kid.ask_for_tokens", "Ask for tokens")}</button>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-base text-slate-400 -mt-1 mb-1">Explain what you did — we'll send a request to your parents so that they can check and confirm.</p>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-500 w-28 flex-shrink-0">{ct("kid.what_did_you_do", "What did you do?")}</label>
                <input className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-500 w-28 flex-shrink-0">Tokens you'd like</label>
                <input className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-500 w-28 flex-shrink-0">{ct("kid.photo_optional", "Photo (optional)")}</label>
                {askImage ? (
                  <div className="flex items-center gap-2">
                    <img src={imageSrc(askImage)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    <button className="text-xs font-bold text-rose-400" onClick={() => setAskImage(null)}>{ct("kid.remove_photo", "Remove")}</button>
                  </div>
                ) : (
                  <label className="text-xs font-medium text-slate-500 bg-slate-100 rounded-lg px-3 py-2 cursor-pointer">
                    {askImageUploading ? "Uploading…" : <><Icon id="ic-photo" size={14} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("kid.add_photo", "Add a photo")}</>}
                    <input type="file" accept="image/*" className="hidden" disabled={askImageUploading} onChange={handleAskPhoto} />
                  </label>
                )}
              </div>
              {askImageError && <div className="text-rose-600 text-xs font-semibold">{askImageError}</div>}
              <div className="flex gap-2">
                <button className={`flex-1 ${kidTheme.primaryBtn} text-white font-bold text-sm rounded-lg py-2`} onClick={() => {
                  const amt = parseInt(amount, 10);
                  if (!title.trim() || !amt || amt <= 0) return;
                  onSubmitRequest(title.trim(), amt, askImage);
                  setTitle(""); setAmount(""); setAskImage(null); setAskImageError(""); setShowAsk(false);
                }}>{ct("kid.send_to_parent", "Send to parent")}</button>
                <button className="px-3 text-slate-400 text-sm" onClick={() => setShowAsk(false)}>{ct("kid.cancel_button", "Cancel")}</button>
              </div>
            </div>
          )}
        </div>
      )}
      {photoProofActivity && (
        <PhotoProofModal
          code={code}
          title={earnDoubleDay ? `${photoProofActivity.label} ${photoProofActivity.tokens} → ${photoProofActivity.tokens * 2}` : `${photoProofActivity.label} +${photoProofActivity.tokens}`}
          onCancel={() => setPhotoProofActivity(null)}
          onSubmit={(image) => { const a = photoProofActivity; setPhotoProofActivity(null); onSubmitRequest(a.label, a.tokens, image); }}
        />
      )}
    </div>
    </div>
  );
}


export function EarnTab({ code, child, requests, allEarnable, habits, habitLog, onToggleHabit, onSubmitRequest, learning, onLearnEarn, onVocabWordResult, rewardSettings, kidTheme, focusTasks, focusLearnEarn, weeklyCapMessage, oinksOn, onEarnOinks, partyEmoji, onSessionFinished, onItemResults, onOpenLearn }) {
  // LB-248 follow-up: the same predicate the earning paths use, so the label and the payout can
  // never disagree about whether today is doubled. rewardSettings is what this file calls the
  // settings object — the existing DoubleTokenDayBanner reads the same one.
  const doubleDayToday = isDoubleTokenDay(child.id, todayStr(), rewardSettings);
  const pendingClaims = requests.filter((r) => r.requestType === "earn" && r.status === "pending").length;
  const learnEarnRef = useRef(null);

  // Scrolls the Learn & Earn section into view when arriving here via the Home teaser
  // (see LearnEarnTeaser and ChildView's onOpenLearnEarn) — it sits below Daily Tasks and
  // Claim Earnings, so without this a kid who tapped "Brain boost time!" would land back
  // at the top of Earn and have to scroll down past both to find what they actually tapped
  // for. App.jsx's own scroll-to-top-on-tab-change effect is guarded against overriding
  // this — see the earnFocusLearn check there.
  useEffect(() => {
    if (focusLearnEarn && learnEarnRef.current) {
      learnEarnRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusLearnEarn]);

  // Journey review P2#6: skipped-setup households used to render Earn as near-silence.
  // One honest card names the gap; Learn & Earn below is populated by default (all games
  // on, starter content auto-served), so the tab is never actually a dead end.
  const nothingConfigured = habitsForChild(habits, child.id).length === 0 && (allEarnable || []).length === 0;

  return (
    <div className="kid-sheet flex flex-col">
      <div className="px-4"><DoubleTokenDayBanner child={child} settings={rewardSettings} /></div>
      {nothingConfigured && (
        <div className="flex gap-3 items-start py-2 px-0.5" data-testid="earn-empty">
          <Piggington expression="fx-thinking" className="h-14 w-auto flex-shrink-0" />
          <p className="text-base text-slate-400">{ct("kid.earn.empty", "No tasks or activities here yet — ask your grown-up to add ways to earn. Your Learn & Earn games are ready below.")}</p>
        </div>
      )}
      {/* Pass N (Alan): on a two-column screen Learn & Earn takes the top of the left column
          and the tasks + claim sit on the right; on a phone the order is tasks, claim, games. */}
      <div className="contents">
        <div className="order-3">

      {/* Learn has its own tab now (Alan, 12 Sep): Earn keeps an ADVERT for it in the Play style,
          as Home does — one line of the latest good news, and a Play button that opens Learn. */}
      <div ref={learnEarnRef}>
        <LearnAdvert learning={learning} rewardSettings={rewardSettings} onOpen={onOpenLearn} />
      </div>
        </div>
      </div>
      <div className="contents">
        <div className="order-1">
      <DailyTasksSection code={code} child={child} habits={habits} habitLog={habitLog} requests={requests} onToggleHabit={onToggleHabit} startExpanded={focusTasks} />
        </div>
        <div className="order-2">
      <ClaimEarningsSection allEarnable={allEarnable} pendingClaims={pendingClaims} onSubmitRequest={onSubmitRequest} kidTheme={kidTheme} code={code} earnDoubleDay={doubleDayToday} />
        </div>
      </div>
    </div>
  );
}
